import { sessionUserSchema } from '@oefy/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Mail, MailCheck, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { SubmitEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, api } from '../lib/api';
import type { ApiErrorCode } from '../lib/api';
import { Backdrop } from '../ui/backdrop';
import { Brand } from '../ui/brand';
import { Button } from '../ui/button';
import { Alert } from '../ui/feedback';
import { TextInput } from '../ui/form';
import { Mascot } from '../ui/mascot';
import { OtpInput } from '../ui/otp-input';
import { SiteFooter } from '../ui/site-footer';
import { cx } from '../ui/styles';
import { useThemeColor } from '../ui/theme-color';
import { useToast } from '../ui/toast';
import { LoginShowcase } from './login-showcase';
import { safeReturnPath, sessionQueryKey } from './session';

const CODE_LENGTH = 6;
const RESEND_AFTER_SECONDS = 30;

const loginMessages: Partial<Record<ApiErrorCode, string>> = {
  invalid_code: 'Deze code klopt niet of is verlopen. Vraag gerust een nieuwe code aan.',
  invalid_request: 'Controleer of je e-mailadres goed is geschreven.',
  rate_limited: 'Je hebt het vaak geprobeerd. Wacht een paar minuten en probeer het opnieuw.',
  service_unavailable:
    'We konden de code nu niet versturen. Probeer het over een paar minuten opnieuw.',
  network_error: 'Oefy is niet bereikbaar. Controleer je internetverbinding.',
};

function loginErrorMessage(error: unknown): string {
  const message = error instanceof ApiError ? loginMessages[error.code] : undefined;
  return message ?? 'Er ging iets mis. Probeer het opnieuw.';
}

function useCooldown(): readonly [number, (seconds: number) => void] {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      setLeft((current) => current - 1);
    }, 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [left]);
  return [left, setLeft];
}

function AccountHelp() {
  return (
    <Link
      to="/abonnement"
      className="ml-auto rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap text-slate-600 outline-none transition hover:bg-white/80 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-brand-600"
    >
      Nog geen account?
    </Link>
  );
}

type Step = 'email' | 'code';

function StepDots({ step }: { readonly step: Step }) {
  return (
    <p className="flex items-center gap-3 text-xs font-medium text-slate-500">
      <span aria-hidden="true" className="flex gap-1.5">
        <span className="h-1.5 w-6 rounded-full bg-brand-600" />
        <span
          className={cx(
            'h-1.5 w-6 rounded-full transition-colors duration-500',
            step === 'code' ? 'bg-brand-600' : 'bg-slate-200',
          )}
        />
      </span>
      Stap {step === 'email' ? 1 : 2} van 2
    </p>
  );
}

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('email');
  const [cooldown, startCooldown] = useCooldown();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  useThemeColor('#fff8e6', '#2394ea');

  const requestCode = useMutation({
    mutationFn: () => api.command('POST', '/auth/code', { email: email.trim() }),
    onSuccess: () => {
      if (step === 'code') {
        toast({ title: 'We hebben een nieuwe code gestuurd' });
      }
      setCode('');
      setStep('code');
      startCooldown(RESEND_AFTER_SECONDS);
    },
  });

  const signIn = useMutation({
    mutationFn: (value: string) =>
      api.post('/auth/session', { email: email.trim(), code: value }, sessionUserSchema),
    onSuccess: (user) => {
      queryClient.setQueryData(sessionQueryKey, user);
      const fallback = user.role === 'staff' ? '/beheer' : '/';
      void navigate(safeReturnPath(searchParams.get('terug')) ?? fallback, { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'invalid_code') {
        setCode('');
      }
    },
  });

  const verify = (value: string) => {
    if (value.length === CODE_LENGTH && !signIn.isPending) {
      signIn.mutate(value);
    }
  };

  const submitEmail = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    requestCode.mutate();
  };

  const submitCode = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    verify(code);
  };

  const backToEmail = () => {
    signIn.reset();
    requestCode.reset();
    setCode('');
    setStep('email');
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      <LoginShowcase />
      <Backdrop className="flex min-h-screen flex-col">
        <div className="flex items-center justify-between gap-4 px-5 pt-5 sm:px-8 sm:pt-7">
          <Brand className="lg:hidden" />
          <AccountHelp />
        </div>
        <div className="flex flex-1 flex-col items-center justify-center px-4 pt-6 pb-10 lg:pt-10">
          <div className="mb-8 flex w-full max-w-md flex-col items-center text-center lg:hidden">
            <Mascot pose="study" size="lg" />
            <h2 className="mt-4 font-rounded text-[2rem] leading-[1.1] font-semibold tracking-tight text-balance text-brand-950">
              Elke dag een beetje beter met Oefy.
            </h2>
            <p className="mt-4 max-w-[22.5rem] text-base leading-relaxed text-balance text-slate-600">
              Speels oefenen op eigen tempo voor kinderen van{' '}
              <span className="whitespace-nowrap">groep 3 t/m 8.</span> Perfect ter voorbereiding op
              Cito, IEP en de Doorstroomtoets.
              <a href="#toelichting" className="text-slate-400 no-underline hover:text-brand-700">
                *
              </a>
            </p>
          </div>
          <div className="relative w-full max-w-md">
            <main className="relative z-10 rounded-4xl bg-white p-7 shadow-warm ring-1 ring-slate-200 sm:p-10">
              <StepDots step={step} />
              <div key={step} className="motion-safe:animate-rise">
                {step === 'email' ? (
                  <form className="mt-7" onSubmit={submitEmail} noValidate>
                    <div className="flex items-center gap-3">
                      <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                        <Mail aria-hidden="true" className="size-5.5" />
                      </span>
                      <h1 className="font-rounded text-[1.75rem] leading-tight font-semibold tracking-tight text-brand-950">
                        Welkom bij Oefy
                      </h1>
                    </div>
                    <p className="mt-3 text-[0.9375rem] leading-relaxed text-slate-600">
                      Vul je e-mailadres in. We sturen je een code om mee in te loggen.
                    </p>
                    <div className="mt-6 space-y-5">
                      <TextInput
                        label="E-mailadres"
                        type="email"
                        name="email"
                        size="lg"
                        icon={<Mail />}
                        autoComplete="email"
                        placeholder="naam@voorbeeld.nl"
                        required
                        autoFocus
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          requestCode.reset();
                        }}
                      />
                      {requestCode.isError ? (
                        <Alert tone="danger">{loginErrorMessage(requestCode.error)}</Alert>
                      ) : null}
                      <Button
                        type="submit"
                        size="lg"
                        className="w-full"
                        busy={requestCode.isPending}
                        disabled={email.trim() === ''}
                      >
                        Stuur mij een code
                        <ArrowRight aria-hidden="true" />
                      </Button>
                    </div>
                  </form>
                ) : (
                  <form className="mt-7" onSubmit={submitCode} noValidate>
                    <div className="flex items-center gap-3">
                      <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                        <MailCheck aria-hidden="true" className="size-5.5" />
                      </span>
                      <h1 className="font-rounded text-[1.75rem] leading-tight font-semibold tracking-tight text-brand-950">
                        Check je inbox
                      </h1>
                    </div>
                    <p className="mt-3 text-[0.9375rem] leading-relaxed text-slate-600">
                      Als <span className="font-semibold text-slate-900">{email.trim()}</span> bij
                      ons bekend is, hebben we een code van 6 cijfers gestuurd. De code is 10
                      minuten geldig.
                    </p>
                    <div className="mt-6 space-y-5">
                      <OtpInput
                        label="Inlogcode"
                        value={code}
                        length={CODE_LENGTH}
                        autoFocus
                        isDisabled={signIn.isPending}
                        error={signIn.isError ? loginErrorMessage(signIn.error) : undefined}
                        onChange={(next) => {
                          setCode(next);
                          if (signIn.isError) {
                            signIn.reset();
                          }
                        }}
                        onComplete={verify}
                      />
                      <Button
                        type="submit"
                        size="lg"
                        className="w-full"
                        busy={signIn.isPending}
                        disabled={code.length !== CODE_LENGTH}
                      >
                        Inloggen
                      </Button>
                    </div>
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 border-t border-slate-100 pt-5 text-sm sm:justify-between">
                      <button
                        type="button"
                        className="flex items-center gap-1.5 rounded-lg font-medium whitespace-nowrap text-slate-600 transition hover:text-slate-900 pointer-coarse:-my-2.5 pointer-coarse:py-2.5"
                        onClick={backToEmail}
                      >
                        <ArrowLeft aria-hidden="true" className="size-4" />
                        Ander e-mailadres
                      </button>
                      <button
                        type="button"
                        className="flex items-center gap-1.5 rounded-lg font-medium whitespace-nowrap text-brand-700 tabular-nums transition hover:text-brand-800 disabled:text-slate-400 pointer-coarse:-my-2.5 pointer-coarse:py-2.5"
                        disabled={requestCode.isPending || cooldown > 0}
                        onClick={() => {
                          signIn.reset();
                          requestCode.mutate();
                        }}
                      >
                        <RotateCcw aria-hidden="true" className="size-4" />
                        {cooldown > 0
                          ? `Nieuwe code over 0:${String(cooldown).padStart(2, '0')}`
                          : 'Nieuwe code sturen'}
                      </button>
                    </div>
                    {requestCode.isError ? (
                      <div className="mt-4">
                        <Alert tone="danger">{loginErrorMessage(requestCode.error)}</Alert>
                      </div>
                    ) : null}
                  </form>
                )}
              </div>
            </main>
          </div>
        </div>
        <SiteFooter disclaimer className="px-5 pb-6 sm:px-8" />
      </Backdrop>
    </div>
  );
}
